import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Archive, Plus, Pencil, Trash2, RefreshCw, ShoppingCart,
  MinusCircle, AlertTriangle, TrendingUp, PackageCheck,
  ChevronDown, ChevronUp, Loader2,
} from 'lucide-react';
import {
  getInventoryItems, addInventoryItem, updateInventoryItem, deleteInventoryItem,
  getInventoryTransactions, inventoryPurchase, inventoryUsage,
} from '../services/adminService';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const today = () => new Date().toISOString().split('T')[0];

const UNITS = ['pc', 'kg', 'g', 'litre', 'ml', 'roll', 'box', 'pair', 'set', 'bag', 'bottle'];

const CATEGORIES = ['Linen & Towels', 'Lighting', 'Cleaning', 'Bathroom', 'Furniture', 'Electronics', 'Kitchen', 'Stationery', 'Maintenance', 'Other'];

const fmtMoney = (n) => `৳${Number(n).toLocaleString('en-BD', { minimumFractionDigits: 0 })}`;

const StockBar = ({ current, low }) => {
  const pct = low > 0 ? Math.min(100, (current / (low * 3)) * 100) : 100;
  const isLow = current <= low;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${isLow ? 'bg-red-500' : 'bg-emerald-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-xs font-semibold tabular-nums ${isLow ? 'text-red-400' : 'text-emerald-400'}`}>
        {Number(current).toLocaleString()}
      </span>
    </div>
  );
};

export default function Inventory() {
  const { user } = useAuth();
  if (user?.role === 'HotelStaff') return <Navigate to="/dashboard" replace />;
  const isHead = ['HotelHead', 'HotelAdmin', 'Admin'].includes(user?.role);

  const [tab, setTab] = useState('items'); // 'items' | 'transactions'
  const [items, setItems] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txnLoading, setTxnLoading] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [itemTxns, setItemTxns] = useState({});

  // Item dialog
  const [itemDialog, setItemDialog] = useState(null); // null | 'add' | item-object
  const [itemForm, setItemForm] = useState({ name: '', category: '', unit: 'pc', lowStockAt: '5' });
  const [itemSaving, setItemSaving] = useState(false);

  // Transaction dialog
  const [txnDialog, setTxnDialog] = useState(null); // null | { item, type: 'purchase'|'usage' }
  const [txnForm, setTxnForm] = useState({ quantity: '', unitPrice: '', date: today(), notes: '' });
  const [txnSaving, setTxnSaving] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try { setItems(await getInventoryItems()); }
    catch { toast.error('Failed to load inventory'); }
    finally { setLoading(false); }
  }, []);

  const loadTxns = useCallback(async () => {
    setTxnLoading(true);
    try { setTxns(await getInventoryTransactions(null, 200)); }
    catch { toast.error('Failed to load transactions'); }
    finally { setTxnLoading(false); }
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { if (tab === 'transactions') loadTxns(); }, [tab, loadTxns]);

  const toggleExpand = async (id) => {
    if (expandedItem === id) { setExpandedItem(null); return; }
    setExpandedItem(id);
    if (!itemTxns[id]) {
      try {
        const t = await getInventoryTransactions(id, 20);
        setItemTxns(prev => ({ ...prev, [id]: t }));
      } catch { /* silent */ }
    }
  };

  const openAddItem = () => {
    setItemForm({ name: '', category: '', unit: 'pc', lowStockAt: '5' });
    setItemDialog('add');
  };

  const openEditItem = (item) => {
    setItemForm({ name: item.name, category: item.category || '', unit: item.unit, lowStockAt: String(item.lowStockAt) });
    setItemDialog(item);
  };

  const handleSaveItem = async (e) => {
    e.preventDefault();
    setItemSaving(true);
    try {
      const payload = {
        name: itemForm.name.trim(),
        category: itemForm.category.trim() || null,
        unit: itemForm.unit,
        lowStockAt: parseFloat(itemForm.lowStockAt) || 5,
      };
      if (itemDialog === 'add') {
        const created = await addInventoryItem(payload);
        setItems(prev => [...prev, created].sort((a, b) =>
          (a.category || '').localeCompare(b.category || '') || a.name.localeCompare(b.name)));
        toast.success('Item added');
      } else {
        const updated = await updateInventoryItem(itemDialog.id, payload);
        setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
        toast.success('Item updated');
      }
      setItemDialog(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setItemSaving(false);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Remove this item from inventory?')) return;
    try {
      await deleteInventoryItem(id);
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success('Item removed');
    } catch (err) { toast.error(err.message); }
  };

  const openTxnDialog = (item, type) => {
    setTxnForm({ quantity: '', unitPrice: '', date: today(), notes: '' });
    setTxnDialog({ item, type });
  };

  const handleSaveTxn = async (e) => {
    e.preventDefault();
    const qty = parseFloat(txnForm.quantity);
    const price = parseFloat(txnForm.unitPrice) || 0;
    if (!qty || qty <= 0) { toast.error('Enter a valid quantity'); return; }
    setTxnSaving(true);
    try {
      const payload = { quantity: qty, unitPrice: price, transactionDate: txnForm.date, notes: txnForm.notes || null };
      let updated;
      if (txnDialog.type === 'purchase') {
        updated = await inventoryPurchase(txnDialog.item.id, payload);
      } else {
        updated = await inventoryUsage(txnDialog.item.id, payload);
      }
      setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
      // refresh item transactions if expanded
      if (expandedItem === txnDialog.item.id) {
        const t = await getInventoryTransactions(txnDialog.item.id, 20);
        setItemTxns(prev => ({ ...prev, [txnDialog.item.id]: t }));
      }
      if (tab === 'transactions') loadTxns();
      toast.success(txnDialog.type === 'purchase' ? 'Purchase recorded' : 'Usage recorded');
      setTxnDialog(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTxnSaving(false);
    }
  };

  const lowStockItems = items.filter(i => i.isLow);
  const totalValue = 0; // optional: could sum from txns

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Archive className="h-6 w-6 text-blue-400" /> Inventory
          </h1>
          <p className="text-slate-400 text-sm mt-1">{items.length} items · {lowStockItems.length} low stock</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="icon" onClick={loadItems} className="text-slate-400 hover:text-white">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={openAddItem} className="bg-blue-600 hover:bg-blue-700 text-white text-sm h-9 px-4">
            <Plus className="h-4 w-4 mr-1.5" /> Add Item
          </Button>
        </div>
      </div>

      {/* Low stock alert */}
      {lowStockItems.length > 0 && (
        <div className="flex items-center gap-3 bg-red-900/20 border border-red-700/40 rounded-xl px-4 py-3 text-sm text-red-300">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 text-red-400" />
          <span>
            <strong>{lowStockItems.length}</strong> item{lowStockItems.length !== 1 ? 's' : ''} running low:{' '}
            {lowStockItems.slice(0, 4).map(i => i.name).join(', ')}
            {lowStockItems.length > 4 ? ` and ${lowStockItems.length - 4} more` : ''}
          </span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-800/60 rounded-lg p-1 w-fit">
        {[['items', 'Items'], ['transactions', 'Transaction History']].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === key ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Items Tab ── */}
      {tab === 'items' && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 text-blue-400 animate-spin" /></div>
            ) : items.length === 0 ? (
              <div className="text-center py-16">
                <Archive className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No inventory items yet.</p>
                <Button onClick={openAddItem} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white text-sm">
                  <Plus className="h-4 w-4 mr-1.5" /> Add First Item
                </Button>
              </div>
            ) : (
              <div>
                {/* Group by category */}
                {(() => {
                  const cats = [...new Set(items.map(i => i.category || 'Uncategorised'))];
                  return cats.map(cat => {
                    const catItems = items.filter(i => (i.category || 'Uncategorised') === cat);
                    return (
                      <div key={cat}>
                        <div className="px-4 py-2 bg-slate-700/30 border-b border-slate-700">
                          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{cat}</span>
                        </div>
                        {catItems.map(item => (
                          <div key={item.id} className="border-b border-slate-700/50 last:border-0">
                            {/* Row */}
                            <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-700/20 transition-colors">
                              <button
                                onClick={() => toggleExpand(item.id)}
                                className="flex-1 flex items-center gap-4 min-w-0 text-left"
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-white font-medium text-sm">{item.name}</span>
                                    {item.isLow && (
                                      <span className="text-xs text-red-400 bg-red-900/30 border border-red-700/40 px-1.5 py-0.5 rounded">Low</span>
                                    )}
                                  </div>
                                  <div className="mt-1.5 max-w-[200px]">
                                    <StockBar current={item.currentStock} low={item.lowStockAt} />
                                  </div>
                                  <p className="text-slate-500 text-xs mt-0.5">
                                    {Number(item.currentStock).toLocaleString()} {item.unit} · Alert at {Number(item.lowStockAt).toLocaleString()}
                                  </p>
                                </div>
                                {expandedItem === item.id
                                  ? <ChevronUp className="h-4 w-4 text-slate-500 flex-shrink-0" />
                                  : <ChevronDown className="h-4 w-4 text-slate-500 flex-shrink-0" />
                                }
                              </button>

                              {/* Actions */}
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button
                                  size="sm"
                                  onClick={() => openTxnDialog(item, 'purchase')}
                                  className="h-7 px-2.5 bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 border border-emerald-700/40 text-xs"
                                  variant="ghost"
                                  title="Record Purchase"
                                >
                                  <ShoppingCart className="h-3 w-3 mr-1" /> Buy
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => openTxnDialog(item, 'usage')}
                                  className="h-7 px-2.5 bg-amber-600/20 hover:bg-amber-600/40 text-amber-400 border border-amber-700/40 text-xs"
                                  variant="ghost"
                                  title="Record Usage"
                                >
                                  <MinusCircle className="h-3 w-3 mr-1" /> Use
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => openEditItem(item)}
                                  className="h-7 w-7 text-slate-400 hover:text-white"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                {isHead && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => handleDeleteItem(item.id)}
                                    className="h-7 w-7 text-slate-500 hover:text-red-400"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                              </div>
                            </div>

                            {/* Expanded: recent transactions for this item */}
                            {expandedItem === item.id && (
                              <div className="bg-slate-900/40 border-t border-slate-700/50 px-6 py-4">
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Recent Transactions</p>
                                {!itemTxns[item.id] ? (
                                  <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-blue-400" /></div>
                                ) : itemTxns[item.id].length === 0 ? (
                                  <p className="text-slate-500 text-sm">No transactions yet.</p>
                                ) : (
                                  <div className="space-y-1.5">
                                    {itemTxns[item.id].map(t => (
                                      <div key={t.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-700/30 last:border-0">
                                        <div className="flex items-center gap-2.5">
                                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                            t.type === 'Purchase'
                                              ? 'bg-emerald-900/40 text-emerald-400'
                                              : 'bg-amber-900/40 text-amber-400'
                                          }`}>
                                            {t.type === 'Purchase' ? '+' : '−'}{Number(t.quantity).toLocaleString()} {t.unit}
                                          </span>
                                          {t.notes && <span className="text-slate-500 text-xs">{t.notes}</span>}
                                        </div>
                                        <div className="text-right">
                                          {t.type === 'Purchase' && t.unitPrice > 0 && (
                                            <span className="text-white text-xs">
                                              {fmtMoney(t.unitPrice)}/{item.unit} = <strong>{fmtMoney(t.totalAmount)}</strong>
                                            </span>
                                          )}
                                          <p className="text-slate-500 text-xs">{t.transactionDate}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Transactions Tab ── */}
      {tab === 'transactions' && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-0">
            {txnLoading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 text-blue-400 animate-spin" /></div>
            ) : txns.length === 0 ? (
              <div className="text-center py-16">
                <TrendingUp className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No transactions recorded yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Date</th>
                      <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Item</th>
                      <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Type</th>
                      <th className="text-right text-xs font-medium text-slate-400 px-4 py-3">Qty</th>
                      <th className="text-right text-xs font-medium text-slate-400 px-4 py-3">Unit Price</th>
                      <th className="text-right text-xs font-medium text-slate-400 px-4 py-3">Total</th>
                      <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {txns.map((t, i) => (
                      <tr key={t.id} className={`border-b border-slate-700/40 ${i % 2 === 0 ? '' : 'bg-slate-800/20'}`}>
                        <td className="px-4 py-3 text-sm text-slate-400 whitespace-nowrap">{t.transactionDate}</td>
                        <td className="px-4 py-3 text-sm text-white font-medium">{t.itemName}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            t.type === 'Purchase'
                              ? 'bg-emerald-900/40 text-emerald-400 border border-emerald-700/40'
                              : 'bg-amber-900/40 text-amber-400 border border-amber-700/40'
                          }`}>
                            {t.type === 'Purchase' ? <PackageCheck className="inline h-3 w-3 mr-1" /> : <MinusCircle className="inline h-3 w-3 mr-1" />}
                            {t.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-white text-right tabular-nums">
                          {t.type === 'Purchase' ? '+' : '−'}{Number(t.quantity).toLocaleString()} {t.unit}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300 text-right tabular-nums">
                          {t.unitPrice > 0 ? fmtMoney(t.unitPrice) : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-right tabular-nums">
                          {t.totalAmount > 0 ? <span className="text-emerald-400">{fmtMoney(t.totalAmount)}</span> : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{t.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Add / Edit Item Dialog ── */}
      <Dialog open={!!itemDialog} onOpenChange={() => setItemDialog(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>{itemDialog === 'add' ? 'Add Inventory Item' : 'Edit Item'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveItem} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <label className="text-sm text-slate-300 font-medium">Item Name *</label>
              <Input
                value={itemForm.name}
                onChange={e => setItemForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Bath Towel"
                required
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm text-slate-300 font-medium">Category</label>
                <select
                  value={itemForm.category}
                  onChange={e => setItemForm(p => ({ ...p, category: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— None —</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm text-slate-300 font-medium">Unit *</label>
                <select
                  value={itemForm.unit}
                  onChange={e => setItemForm(p => ({ ...p, unit: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-slate-300 font-medium">Low Stock Alert (qty)</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={itemForm.lowStockAt}
                onChange={e => setItemForm(p => ({ ...p, lowStockAt: e.target.value }))}
                className="bg-slate-700 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-500">Show alert when stock falls to or below this level.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setItemDialog(null)} className="border-slate-600">Cancel</Button>
              <Button type="submit" disabled={itemSaving} className="bg-blue-600 hover:bg-blue-700">
                {itemSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                {itemDialog === 'add' ? 'Add Item' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Purchase / Usage Dialog ── */}
      {txnDialog && (
        <Dialog open={true} onOpenChange={() => setTxnDialog(null)}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {txnDialog.type === 'purchase'
                  ? <ShoppingCart className="h-5 w-5 text-emerald-400" />
                  : <MinusCircle className="h-5 w-5 text-amber-400" />}
                {txnDialog.type === 'purchase' ? 'Record Purchase' : 'Record Usage'} — {txnDialog.item.name}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSaveTxn} className="space-y-4 pt-2">
              {/* Current stock info */}
              <div className="bg-slate-700/50 rounded-lg px-3 py-2.5 flex justify-between text-sm">
                <span className="text-slate-400">Current Stock</span>
                <span className={`font-semibold ${txnDialog.item.isLow ? 'text-red-400' : 'text-emerald-400'}`}>
                  {Number(txnDialog.item.currentStock).toLocaleString()} {txnDialog.item.unit}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm text-slate-300 font-medium">
                    Quantity ({txnDialog.item.unit}) *
                  </label>
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={txnForm.quantity}
                    onChange={e => setTxnForm(p => ({ ...p, quantity: e.target.value }))}
                    placeholder="0"
                    required
                    autoFocus
                    className="bg-slate-700 border-slate-600 text-white text-lg font-mono"
                  />
                </div>
                {txnDialog.type === 'purchase' && (
                  <div className="space-y-1.5">
                    <label className="text-sm text-slate-300 font-medium">Unit Price (৳)</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={txnForm.unitPrice}
                      onChange={e => setTxnForm(p => ({ ...p, unitPrice: e.target.value }))}
                      placeholder="0"
                      className="bg-slate-700 border-slate-600 text-white text-lg font-mono"
                    />
                  </div>
                )}
              </div>

              {/* Live total */}
              {txnDialog.type === 'purchase' && txnForm.quantity && txnForm.unitPrice && (
                <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-lg px-4 py-3 flex justify-between items-center">
                  <span className="text-sm text-slate-300">
                    {txnForm.quantity} {txnDialog.item.unit} × {fmtMoney(txnForm.unitPrice)}
                  </span>
                  <span className="text-emerald-300 font-bold text-lg">
                    = {fmtMoney(parseFloat(txnForm.quantity) * parseFloat(txnForm.unitPrice))}
                  </span>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-sm text-slate-300 font-medium">Date *</label>
                <Input
                  type="date"
                  value={txnForm.date}
                  onChange={e => setTxnForm(p => ({ ...p, date: e.target.value }))}
                  required
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm text-slate-300 font-medium">Notes <span className="text-slate-500 font-normal">(optional)</span></label>
                <Input
                  value={txnForm.notes}
                  onChange={e => setTxnForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder={txnDialog.type === 'purchase' ? 'Supplier, invoice no…' : 'Room no., reason…'}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setTxnDialog(null)} className="border-slate-600">Cancel</Button>
                <Button
                  type="submit"
                  disabled={txnSaving}
                  className={txnDialog.type === 'purchase'
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : 'bg-amber-600 hover:bg-amber-700 text-white'}
                >
                  {txnSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                  {txnDialog.type === 'purchase' ? 'Record Purchase' : 'Record Usage'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
